// KHLIFI HOUCEM / FORMATEUR CLOUD && DEVSECOPS
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { interviewAPI } from "@/lib/api";
import { WorkspaceLayout } from "@/components/layout/WorkspaceLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  ChevronLeft, Loader2, Trophy, TrendingUp, TrendingDown, AlertTriangle,
  Download, Shield, Target, CheckCircle2, XCircle, Clock, User
} from "lucide-react";
import { toast } from "sonner";
import { formatDate } from "@/lib/utils";
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer } from "recharts";

const REC_LABELS = { rejected: "Rejeté", review: "À revoir", retained: "Retenu", excellent: "Excellent" };
const REC_COLORS = { rejected: "bg-red-100 text-red-700 border-red-200", review: "bg-yellow-100 text-yellow-700 border-yellow-200", retained: "bg-green-100 text-green-700 border-green-200", excellent: "bg-emerald-100 text-emerald-700 border-emerald-200" };
const REC_ICONS = { rejected: XCircle, review: AlertTriangle, retained: CheckCircle2, excellent: Trophy };
const OBS_LABELS = { communication: "Communication", clarity: "Clarté", rigor: "Rigueur", autonomy: "Autonomie", depth: "Profondeur" };

export default function InterviewSummaryPage() {
  const { workspaceId, interviewId } = useParams();
  const navigate = useNavigate();
  const [interview, setInterview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [overrideDialog, setOverrideDialog] = useState(false);
  const [overrideRec, setOverrideRec] = useState("");
  const [overrideJust, setOverrideJust] = useState("");

  useEffect(() => { loadData(); }, [interviewId]);

  const loadData = async () => {
    try {
      const res = await interviewAPI.getInterview(interviewId);
      setInterview(res.data);
    } catch (e) { toast.error("Erreur"); navigate(-1); } finally { setLoading(false); }
  };

  const handleOverride = async () => {
    if (!overrideRec || !overrideJust.trim()) { toast.error("Justification requise"); return; }
    try {
      await interviewAPI.overrideRecommendation(interviewId, { recommendation: overrideRec, justification: overrideJust });
      toast.success("Recommandation modifiée");
      setOverrideDialog(false);
      loadData();
    } catch (e) { toast.error("Erreur"); }
  };

  if (loading || !interview) {
    return <WorkspaceLayout><div className="page-container"><div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div></div></WorkspaceLayout>;
  }

  const recommendation = interview.recommendation_override || interview.recommendation;
  const RecIcon = REC_ICONS[recommendation] || Target;

  // Build radar data
  const radarData = interview.sections.map(s => {
    const pct = s.section_max > 0 ? (s.section_score / s.section_max) * 100 : 0;
    return { subject: s.title, score: Math.round(pct), fullMark: 100 };
  });

  // Build observation radar data
  const obsData = Object.entries(interview.observations || {}).map(([key, value]) => ({
    subject: OBS_LABELS[key] || key, score: (value / 5) * 100, fullMark: 100,
  }));

  return (
    <WorkspaceLayout>
      <div className="page-container animate-fade-in">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/workspace/${workspaceId}/interviews/candidates/${interview.candidate_id}`)}>
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-semibold">Synthèse d'entretien</h1>
            <p className="text-sm text-muted-foreground">
              {interview.candidate_name} · {interview.template_title} · {interview.completed_at ? formatDate(interview.completed_at) : ""}
            </p>
          </div>
        </div>

        {/* Top Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          {/* Score */}
          <Card className="md:col-span-1">
            <CardContent className="pt-6 text-center">
              <div className="text-5xl font-bold text-primary">{Math.round(interview.weighted_percentage || interview.percentage)}%</div>
              <p className="text-sm text-muted-foreground mt-1">{interview.total_score?.toFixed(1)} / {interview.max_score?.toFixed(1)} pts</p>
              <Progress value={interview.weighted_percentage || interview.percentage} className="mt-3" />
            </CardContent>
          </Card>

          {/* Recommendation */}
          <Card className={`md:col-span-1 border-2 ${REC_COLORS[recommendation] || ""}`}>
            <CardContent className="pt-6 text-center">
              <RecIcon className="h-10 w-10 mx-auto mb-2" />
              <p className="text-lg font-bold">{REC_LABELS[recommendation]}</p>
              {interview.recommendation_override && (
                <p className="text-xs text-muted-foreground mt-1">Override par {interview.override_justification?.substring(0, 50)}</p>
              )}
              <Button size="sm" variant="outline" className="mt-3" onClick={() => { setOverrideRec(""); setOverrideJust(""); setOverrideDialog(true); }}>
                Modifier
              </Button>
            </CardContent>
          </Card>

          {/* Meta */}
          <Card className="md:col-span-1">
            <CardContent className="pt-6 space-y-2 text-sm">
              <div className="flex items-center gap-2"><User className="h-4 w-4 text-muted-foreground" /><span>{interview.interviewer_name}</span></div>
              <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-muted-foreground" /><span>{interview.actual_duration_minutes} min</span></div>
              <div className="flex items-center gap-2"><Target className="h-4 w-4 text-muted-foreground" /><span>{interview.sections.reduce((a, s) => a + s.responses.filter(r => r.status !== "skipped").length, 0)} questions évaluées</span></div>
            </CardContent>
          </Card>

          {/* Forces / Faiblesses */}
          <Card className="md:col-span-1">
            <CardContent className="pt-6 space-y-2">
              {interview.strengths?.length > 0 && (
                <div><p className="text-xs font-semibold text-green-600 flex items-center gap-1"><TrendingUp className="h-3 w-3" />Points forts</p>
                  {interview.strengths.map((s, i) => <Badge key={i} variant="outline" className="text-xs mr-1 mt-1 bg-green-50">{s}</Badge>)}</div>
              )}
              {interview.weaknesses?.length > 0 && (
                <div className="mt-2"><p className="text-xs font-semibold text-red-600 flex items-center gap-1"><TrendingDown className="h-3 w-3" />Faiblesses</p>
                  {interview.weaknesses.map((w, i) => <Badge key={i} variant="outline" className="text-xs mr-1 mt-1 bg-red-50">{w}</Badge>)}</div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Technical Radar */}
          <Card>
            <CardHeader><CardTitle className="text-base">Compétences techniques</CardTitle></CardHeader>
            <CardContent>
              {radarData.length > 2 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <RadarChart data={radarData} cx="50%" cy="50%" outerRadius="70%">
                    <PolarGrid stroke="hsl(var(--border))" />
                    <PolarAngleAxis dataKey="subject" tick={{ fontSize: 11 }} />
                    <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 10 }} />
                    <Radar name="Score" dataKey="score" stroke="hsl(var(--primary))" fill="hsl(var(--primary))" fillOpacity={0.3} strokeWidth={2} />
                  </RadarChart>
                </ResponsiveContainer>
              ) : (
                <div className="space-y-3">
                  {radarData.map(d => (
                    <div key={d.subject} className="flex items-center gap-3">
                      <span className="text-sm w-32 truncate">{d.subject}</span>
                      <Progress value={d.score} className="flex-1" />
                      <span className="text-sm font-medium w-10 text-right">{d.score}%</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Observation Radar */}
          <Card>
            <CardHeader><CardTitle className="text-base">Observations comportementales</CardTitle></CardHeader>
            <CardContent>
              {obsData.length > 2 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <RadarChart data={obsData} cx="50%" cy="50%" outerRadius="70%">
                    <PolarGrid stroke="hsl(var(--border))" />
                    <PolarAngleAxis dataKey="subject" tick={{ fontSize: 11 }} />
                    <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 10 }} />
                    <Radar name="Score" dataKey="score" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.3} strokeWidth={2} />
                  </RadarChart>
                </ResponsiveContainer>
              ) : (
                <div className="space-y-3">
                  {obsData.map(d => (
                    <div key={d.subject} className="flex items-center gap-3">
                      <span className="text-sm w-32">{d.subject}</span>
                      <Progress value={d.score} className="flex-1" />
                      <span className="text-sm font-medium w-10 text-right">{d.score}%</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Section Breakdown */}
        <Card className="mb-6">
          <CardHeader><CardTitle className="text-base">Détail par section</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {interview.sections.map((s, idx) => {
              const pct = s.section_max > 0 ? Math.round((s.section_score / s.section_max) * 100) : 0;
              return (
                <div key={s.section_id || idx}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-sm">{s.title}</span>
                    <span className="text-sm">{s.section_score?.toFixed(1)} / {s.section_max?.toFixed(1)} ({pct}%)</span>
                  </div>
                  <Progress value={pct} className={`h-2 ${pct >= 70 ? "[&>div]:bg-green-500" : pct >= 50 ? "[&>div]:bg-yellow-500" : "[&>div]:bg-red-500"}`} />
                  <div className="mt-1 text-xs text-muted-foreground">
                    {s.responses.filter(r => r.status !== "skipped").length}/{s.responses.length} questions évaluées · Poids ×{s.weight}
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Global Notes */}
        {interview.global_notes && (
          <Card className="mb-6">
            <CardHeader><CardTitle className="text-base">Notes globales</CardTitle></CardHeader>
            <CardContent><p className="text-sm whitespace-pre-wrap">{interview.global_notes}</p></CardContent>
          </Card>
        )}

        {/* Risk Areas */}
        {interview.risk_areas?.length > 0 && (
          <Card className="mb-6 border-yellow-200 bg-yellow-50/50">
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-yellow-600" />Zones à risque</CardTitle></CardHeader>
            <CardContent>
              <ul className="list-disc list-inside text-sm space-y-1">
                {interview.risk_areas.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Override Dialog */}
      <Dialog open={overrideDialog} onOpenChange={setOverrideDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Modifier la recommandation</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Nouvelle recommandation</Label>
              <Select value={overrideRec} onValueChange={setOverrideRec}>
                <SelectTrigger><SelectValue placeholder="Choisir..." /></SelectTrigger>
                <SelectContent>{Object.entries(REC_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Justification *</Label>
              <Textarea value={overrideJust} onChange={(e) => setOverrideJust(e.target.value)} rows={3} placeholder="Pourquoi modifier la recommandation automatique ?" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setOverrideDialog(false)}>Annuler</Button>
              <Button onClick={handleOverride}>Appliquer</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </WorkspaceLayout>
  );
}
